import { useState, useEffect, useRef, useMemo, RefObject } from "react";
import { BlueprintCanvasConfiguration } from "../../utils/BlueprintCanvasUtils";
import { CARD_X_STEP, CARD_Y_STEP, CANVAS_Y_OFFSET } from "../utils/constants";

interface ViewportBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface UseViewportCullingProps {
  canvasConfiguration: BlueprintCanvasConfiguration;
  cardXStart: number;
  enabled: boolean; // Allow disabling for small blueprints
  canvasRef: RefObject<HTMLDivElement>; // Reference to the canvas container
}

/**
 * Hook to calculate which steps are visible in the canvas viewport
 * This enables viewport culling for large blueprints to improve performance
 */
export const useViewportCulling = ({
  canvasConfiguration,
  cardXStart,
  enabled,
  canvasRef,
}: UseViewportCullingProps) => {
  const [viewportBounds, setViewportBounds] = useState<ViewportBounds>({
    minX: 0,
    maxX: Infinity,
    minY: 0,
    maxY: Infinity,
  });

  const rafRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);
  const THROTTLE_MS = 100; // Throttle viewport updates to every 100ms

  useEffect(() => {
    const canvasElement = canvasRef.current;

    if (!enabled || !canvasElement) {
      // If disabled or no canvas ref, show all steps
      setViewportBounds({
        minX: 0,
        maxX: Infinity,
        minY: 0,
        maxY: Infinity,
      });
      return;
    }

    const updateViewport = () => {
      const now = Date.now();

      // Throttle updates
      if (now - lastUpdateRef.current < THROTTLE_MS) {
        rafRef.current = requestAnimationFrame(updateViewport);
        return;
      }

      lastUpdateRef.current = now;

      const scrollX = canvasElement.scrollLeft;
      const scrollY = canvasElement.scrollTop;
      const containerWidth = canvasElement.clientWidth;
      const containerHeight = canvasElement.clientHeight;

      // Add buffer zones (20 cards in each direction) to preload nearby steps
      // This ensures Ctrl+F search still works while maintaining performance benefits
      const BUFFER_X = CARD_X_STEP * 10;
      const BUFFER_Y = CARD_Y_STEP * 10;

      // Calculate visible indices with buffer
      const minX = Math.max(0, Math.floor((scrollX - cardXStart - BUFFER_X) / CARD_X_STEP));
      const maxX = Math.ceil((scrollX + containerWidth - cardXStart + BUFFER_X) / CARD_X_STEP);
      const minY = Math.max(0, Math.floor((scrollY - CANVAS_Y_OFFSET - BUFFER_Y) / CARD_Y_STEP));
      const maxY = Math.ceil(
        (scrollY + containerHeight - CANVAS_Y_OFFSET + BUFFER_Y) / CARD_Y_STEP
      );

      setViewportBounds({
        minX,
        maxX,
        minY,
        maxY,
      });
    };

    // Initial update
    updateViewport();

    // Update on scroll and resize
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateViewport);
    };

    // Listen to canvas container scroll, not window scroll
    canvasElement.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      canvasElement.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [enabled, cardXStart, canvasRef]);

  // Calculate visible step IDs based on viewport bounds
  const visibleStepIDs = useMemo(() => {
    if (!enabled) {
      return new Set(Object.keys(canvasConfiguration.stepPlacements));
    }

    const visible = new Set<string>();

    Object.entries(canvasConfiguration.stepPlacements).forEach(([stepID, placement]) => {
      const isInViewport =
        placement.xIndex >= viewportBounds.minX &&
        placement.xIndex <= viewportBounds.maxX &&
        placement.yIndex >= viewportBounds.minY &&
        placement.yIndex <= viewportBounds.maxY;

      if (isInViewport) {
        visible.add(stepID);
      }
    });

    return visible;
  }, [canvasConfiguration.stepPlacements, viewportBounds, enabled]);

  return {
    visibleStepIDs,
    isStepVisible: (stepID: string) => visibleStepIDs.has(stepID),
  };
};
